import { useEffect, useState } from "react"
import { Button, Card, message } from "antd"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { CloudUploadOutlined } from "@ant-design/icons"
import apiClient from "../../../Utils/apiClient"
import PanelLayout from "../../../Layout/PanelLayout"
import TableBox from "../../../components/TableBox/TableBox"
import EmptyState from "../../../components/EmptyState/EmptyState"
import PageSkeleton from "../../../components/PageSkeleton/PageSkeleton"
import { useHeaderContext } from "../../../Utils/HeaderContext"
import { uniqueUpdateUsers } from "../../../Utils/updateCounts"
import {
  chocoUpgradeCommands,
  wingetUpgradeCommands,
} from "../../../Utils/wingetPackage"
import "../../Operations/Operations.scss"

const InfoRow = ({ label, children }) => (
  <div className="info-grid__row">
    <dt>{label}</dt>
    <dd>{children || "—"}</dd>
  </div>
)

const GlobalSoftwareManagement = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { state } = useLocation()
  const { setRender, setTaskId } = useHeaderContext()
  const [patchData, setPatchData] = useState(state?.record || null)
  const [loading, setLoading] = useState(!state?.record)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [updating, setUpdating] = useState(false)

  useEffect(() => {
    const load = async () => {
      if (state?.record) {
        setPatchData(state.record)
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        const data = await apiClient("devices/updates/apps")
        const match = (Array.isArray(data) ? data : []).find(
          (item) => String(item._id) === String(id)
        )
        setPatchData(match || null)
      } catch (error) {
        console.error("Failed to load catalog app", error)
        setPatchData(null)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id, state?.record])

  const devices = uniqueUpdateUsers(patchData?.users).map((item) => ({
    ...item,
    key: item.key || `${item.hostname || ""}::${item.user || ""}`,
  }))
  const source = String(patchData?.source || "").toLowerCase() === "winget" ? "winget" : "choco"

  const queueHosts = async (hostnames) => {
    const commands =
      source === "choco"
        ? chocoUpgradeCommands([patchData])
        : wingetUpgradeCommands([patchData])
    if (!commands.length) {
      message.warning("Could not resolve the package ID")
      return
    }
    setUpdating(true)
    try {
      const appData = [
        {
          appName: patchData.appName,
          name: patchData.name,
          availableVersion: patchData.availableVersion,
          currentVersion: patchData.currentVersion,
          source: patchData.source,
        },
      ]
      for (const hostname of hostnames) {
        for (const command of commands) {
          const response = await apiClient(`devices/${hostname}/create-task`, "POST", {
            entityType: `upgrade${source}`,
            hostname,
            name: command,
            command,
            appData,
          })
          if (response?.taskId) {
            setTaskId(response.taskId)
            setRender((flag) => !flag)
          }
        }
        message.success(`Queued app update for ${hostname}`)
      }
      setSelectedRowKeys([])
    } catch (error) {
      console.error("Failed to queue app update", error)
      message.error("Failed to queue app update")
    } finally {
      setUpdating(false)
    }
  }

  if (loading) {
    return (
      <PanelLayout title="App update"
      subtitle="Queue an application update on selected devices.">
        <PageSkeleton cards={1} rows={6} />
      </PanelLayout>
    )
  }

  if (!patchData) {
    return (
      <PanelLayout title="App update"
      subtitle="Queue an application update on selected devices.">
        <EmptyState
          title="Update not found"
          description="This catalog entry is no longer pending, or the link is out of date."
          actionLabel="Back to catalog"
          onAction={() => navigate("/global-patches")}
        />
      </PanelLayout>
    )
  }

  return (
    <PanelLayout
      title={patchData.appName || patchData.name || "App update"}
      subtitle="Queue this application update on selected devices."
      actions={
        <Button
          type="primary"
          icon={<CloudUploadOutlined />}
          loading={updating}
          onClick={() =>
            queueHosts(
              selectedRowKeys.length
                ? devices
                    .filter((item) => selectedRowKeys.includes(item.key))
                    .map((item) => item.hostname)
                : devices.map((item) => item.hostname)
            )
          }
        >
          {selectedRowKeys.length
            ? `Update ${selectedRowKeys.length} selected`
            : `Update ${devices.length} devices`}
        </Button>
      }
    >
      <Card className="info-grid-card ops-card" title="Details" style={{ marginBottom: 16 }}>
        <dl className="info-grid">
          <InfoRow label="Package">{patchData.name}</InfoRow>
          <InfoRow label="Source">{source === "winget" ? "Winget" : "Chocolatey"}</InfoRow>
          <InfoRow label="Installed">{patchData.currentVersion}</InfoRow>
          <InfoRow label="Available">{patchData.availableVersion}</InfoRow>
        </dl>
      </Card>

      <TableBox
        title="Devices"
        page="Catalog-Apps-devices"
        columns={[
          { title: "Hostname", dataIndex: "hostname", key: "hostname", render: (v) => v || "—" },
          { title: "User", dataIndex: "user", key: "user", render: (v) => v || "—" },
        ]}
        data={devices}
        pagination={{ showSizeChanger: true }}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
      />
    </PanelLayout>
  )
}

export default GlobalSoftwareManagement
